import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check, X, ClipboardCheck, Target, Mail, CalendarDays, Star, Video, Flag, ChevronRight, History,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, StatusBadge, EmptyState, Spinner, Pill, UserAvatar } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import { fmtDate, fmtTime } from "@/lib/constants";

function DetailBlock({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">{label}</p>
      {children}
    </div>
  );
}

function NeedDetailSheet({ open, onOpenChange, need, context, loading, busy, onDecide }) {
  const { t } = useTranslation();
  const emp = context?.employee;
  const sessions = context?.sessions || [];
  const otherGoals = context?.other_goals || [];
  const completed = sessions.filter((s) => s.status === "completed").length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-y-auto" data-testid="need-detail-sheet">
        {!need ? null : (
          <div className="flex flex-col min-h-full">
            {/* Header */}
            <div className="p-6 border-b border-border bg-gradient-to-b from-secondary/40 to-transparent">
              <div className="flex items-start gap-2 flex-wrap">
                <h2 className="text-xl font-semibold tracking-tight text-foreground">{need.title}</h2>
                <StatusBadge status={need.status} />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Pill className="bg-primary/10 text-primary"><Target size={11} className="mr-1" />{need.field}</Pill>
                {need.urgency && <Pill className="bg-secondary text-foreground"><Flag size={11} className="mr-1" />{String(need.urgency).replace(/_/g, " ")}</Pill>}
                <span>· {t("hr.approvals.submitted", { date: fmtDate(need.created_at) })}</span>
              </div>
            </div>

            <div className="p-6 space-y-6 flex-1">
              {/* Objective */}
              <DetailBlock label={t("hr.approvals.objective")}>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{need.objective || "—"}</p>
              </DetailBlock>

              {loading ? (
                <div className="grid place-items-center py-8"><Spinner className="text-primary w-6 h-6" /></div>
              ) : (
                <>
                  {/* Employee */}
                  <DetailBlock label={t("hr.approvals.requestedBy")}>
                    <div className="rounded-xl border border-border bg-secondary/30 p-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar src={fileUrl(emp?.picture)} name={emp?.name || need.employee_name} size={44} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{emp?.name || need.employee_name}</p>
                          {emp?.email && <p className="text-xs text-muted-foreground truncate inline-flex items-center gap-1"><Mail size={12} /> {emp.email}</p>}
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                        <div><p className="text-lg font-semibold tabular text-foreground">{emp?.bookings_count ?? 0}</p><p className="text-[11px] text-muted-foreground">{t("hr.approvals.sessions")}</p></div>
                        <div><p className="text-lg font-semibold tabular text-foreground">{completed}</p><p className="text-[11px] text-muted-foreground">{t("hr.approvals.completed")}</p></div>
                        <div><p className="text-lg font-semibold tabular text-foreground">{otherGoals.length + 1}</p><p className="text-[11px] text-muted-foreground">{t("hr.approvals.goals")}</p></div>
                      </div>
                      {emp?.created_at && <p className="text-[11px] text-muted-foreground mt-3 inline-flex items-center gap-1"><CalendarDays size={12} /> {t("hr.approvals.joined", { date: fmtDate(emp.created_at) })}</p>}
                    </div>
                  </DetailBlock>

                  {/* Other goals */}
                  {otherGoals.length > 0 && (
                    <DetailBlock label={t("hr.approvals.otherGoals", { count: otherGoals.length })}>
                      <div className="space-y-2">
                        {otherGoals.map((g) => (
                          <div key={g.need_id} className="flex items-center gap-2 rounded-lg border border-border p-3">
                            <span className="text-sm text-foreground flex-1 truncate">{g.title}</span>
                            <Pill className="bg-secondary text-muted-foreground">{g.field}</Pill>
                            <StatusBadge status={g.status} />
                          </div>
                        ))}
                      </div>
                    </DetailBlock>
                  )}

                  {/* Session history */}
                  <DetailBlock label={t("hr.approvals.sessionHistory")}>
                    {sessions.length ? (
                      <div className="space-y-2">
                        {sessions.map((s) => (
                          <div key={s.booking_id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                            <span className="grid place-items-center w-8 h-8 rounded-md bg-secondary text-muted-foreground shrink-0"><Video size={14} /></span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-foreground truncate">{s.topic || s.field}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{s.expert_name} · {fmtDate(s.start)} {s.start ? `· ${fmtTime(s.start)}` : ""}</p>
                            </div>
                            {s.rating ? (
                              <span className="inline-flex items-center gap-1 text-xs text-warning shrink-0"><Star size={12} className="fill-warning" /> {s.rating}.0</span>
                            ) : <StatusBadge status={s.status} />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-lg border border-dashed border-border p-4">
                        <History size={15} /> {t("hr.approvals.noHistory")}
                      </div>
                    )}
                  </DetailBlock>
                </>
              )}
            </div>

            {/* Footer actions */}
            {need.status === "pending" && (
              <div className="sticky bottom-0 border-t border-border bg-card p-4 flex gap-2">
                <Button data-testid="sheet-reject" variant="outline" disabled={busy} onClick={() => onDecide(need.need_id, "reject")} className="flex-1 rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5">
                  <X size={15} /> {t("hr.approvals.decline")}
                </Button>
                <Button data-testid="sheet-approve" disabled={busy} onClick={() => onDecide(need.need_id, "approve")} className="flex-1 rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
                  {busy ? <Spinner /> : <><Check size={15} /> {t("hr.approvals.approve")}</>}
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function HrApprovals() {
  const { t } = useTranslation();
  const [needs, setNeeds] = useState(null);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(null);
  const [context, setContext] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);

  const load = () => api.get("/company/needs").then(({ data }) => setNeeds(data)).catch(() => setNeeds([]));
  useEffect(() => { load(); }, []);

  const openDetail = async (n) => {
    setSelected(n);
    setContext(null);
    setCtxLoading(true);
    try { const { data } = await api.get(`/company/needs/${n.need_id}/context`); setContext(data); }
    catch (err) { console.error("Goal context fetch failed:", err); } finally { setCtxLoading(false); }
  };

  const decide = async (id, action, closeSheet = false) => {
    setBusy(id);
    try {
      await api.post(`/company/needs/${id}/${action}`);
      toast.success(action === "approve" ? t("hr.approvals.toastApproved") : t("hr.approvals.toastDeclined"));
      if (closeSheet) setSelected(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  const filtered = (needs || []).filter((n) => tab === "all" ? true : n.status === tab);
  const pendingCount = (needs || []).filter((n) => n.status === "pending").length;

  return (
    <AppShell>
      <PageHeader eyebrow={t("hr.eyebrow")} title={t("hr.approvals.title")} description={t("hr.approvals.subtitle")} />

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="rounded-lg">
          <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-md gap-1.5">
            {t("hr.approvals.tabPending")} {pendingCount > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">{pendingCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved" className="rounded-md">{t("hr.approvals.tabApproved")}</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all" className="rounded-md">{t("hr.approvals.tabAll")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {needs === null ? (
        <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ClipboardCheck} title={tab === "pending" ? t("hr.approvals.emptyPending") : t("hr.approvals.emptyOther")} description={tab === "pending" ? t("hr.approvals.emptyPendingDesc") : t("hr.approvals.emptyOtherDesc")} />
      ) : (
        <div className="space-y-3">
          {filtered.map((n, i) => (
            <div key={n.need_id} data-testid={`need-${n.need_id}`} className={`card-milled rounded-[0.8rem] p-5 sm:p-6 animate-fade-up d${(i % 6) + 1} transition-colors hover:border-primary/30`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <button
                  type="button"
                  data-testid={`view-need-${n.need_id}`}
                  onClick={() => openDetail(n)}
                  className="min-w-0 flex-1 text-left group"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-medium group-hover:text-primary transition-colors">{n.title}</h3>
                    <StatusBadge status={n.status} />
                    <ChevronRight size={15} className="text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed line-clamp-2">{n.objective}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{n.employee_name}</span>
                    <span>·</span>
                    <Pill className="bg-primary/10 text-primary"><Target size={11} className="mr-1" />{n.field}</Pill>
                    <span>· {t("hr.approvals.submitted", { date: fmtDate(n.created_at) })}</span>
                  </div>
                </button>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" data-testid={`open-need-${n.need_id}`} onClick={() => openDetail(n)} className="rounded-lg gap-1.5 bg-card">
                    {t("common.view")}
                  </Button>
                  {n.status === "pending" && (
                    <>
                      <Button data-testid={`reject-${n.need_id}`} variant="outline" disabled={busy === n.need_id} onClick={() => decide(n.need_id, "reject")} className="rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5">
                        <X size={15} /> {t("hr.approvals.decline")}
                      </Button>
                      <Button data-testid={`approve-${n.need_id}`} disabled={busy === n.need_id} onClick={() => decide(n.need_id, "approve")} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
                        {busy === n.need_id ? <Spinner /> : <><Check size={15} /> {t("hr.approvals.approve")}</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <NeedDetailSheet
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        need={selected}
        context={context}
        loading={ctxLoading}
        busy={busy === selected?.need_id}
        onDecide={(id, action) => decide(id, action, true)}
      />
    </AppShell>
  );
}
